import { useState } from 'react';

export default function FAQSection() {
  const [activeCategory, setActiveCategory] = useState('general');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const categories = [
    { id: 'general', label: 'General Questions' },
    { id: 'carpet', label: 'Carpet Cleaning' },
    { id: 'furniture', label: 'Furniture Cleaning' }
  ];

  const faqs = {
    general: [
      {
        question: "How often should I steam clean my carpets or furniture?",
        answer: "It all depends on how much you use it. If it's the carpeting in your home, we recommend to steam clean it twice a year. In high traffic areas such as an office, a restaurant, or any other commercial property, we recommend steam cleaning carpets at least 2-4 times a year. Furniture doesn't require steam cleaning as frequently. Once a year is enough for most home furniture and cleaning twice a year is recommended for furniture in a commercial environment."
      },
      {
        question: "How long does it take to steam clean carpets or furniture?",
        answer: "It all depends on the size of the cleaning surface. For example, it takes about 1.5-2 hours to steam clean 1000 sq.ft. of carpeted area. It takes about 1 hour to steam clean a regular 2-3 seat sofa."
      },
      {
        question: "Is it safe to be around while steam cleaning?",
        answer: "Yes, it's totally safe to be around while steam cleaning. We use only the best, environmentally friendly cleaning products that are absolutely allergy free and safe for people and pets."
      },
      {
        question: "Can you take away urine smell?",
        answer: "Yes, we can remove urine smell by using biological treatment cleaning product. This is biologically enhanced hard surface cleaner, that utilizes special bacterial cultures, containing the ideal synergized blend of selectively adapted micro-organisms. Each strain has been developed specifically to accelerate degradation of grease, fats, proteins, starches, sugars, oils, and URINE."
      }
    ],
    carpet: [
      {
        question: "How much does carpet cleaning cost?",
        answer: "Pricing starts at $99 for carpet cleaning and varies depending on the number of rooms with carpet that require cleaning. Area rug cleaning starts at $1.00/square ft."
      },
      {
        question: "How does steam cleaning carpets work?",
        answer: "We use a truck-mounted hot water extraction system. Simply put, water is heated past boiling point and then injected into the fibres of the carpet or area rug. The high-powered vacuum unit then sucks out that water back out, along with the dirt, bacteria, and allergens."
      },
      {
        question: "Do I need to do anything to prepare for carpet cleaning?",
        answer: "All the furniture in the room needs to be removed (or at least adjusted to free up as much floor space as possible). If there is any furniture left in the room, those areas of the carpet are not going to receive the same level of deep cleaning as the rest of the floor. If you are unable to move your furniture, we offer an additional furniture moving service."
      },
      {
        question: "Can professional carpet cleaning get rid of fleas?",
        answer: "Yes. The hot water can effectively kill fleas and the powerful suction from our truck-mounted system pulls the flea bodies and their eggs out of the fibres of the carpet."
      },
      {
        question: "When can I walk on my carpets after steam cleaning?",
        answer: "We recommend waiting 4-5 hours before resuming regular activities in your carpeted areas. If you need to access the space, you should ensure that your feet are clean so as not to introduce any dirt and risk staining your freshly cleaned carpets."
      }
    ],
    furniture: [
      {
        question: "Why should I get my furniture professionally cleaned instead of just doing it myself?",
        answer: "While you can easily find a hand-held steam cleaning unit or purchase an upholstery cleaning spray, you can not guarantee the same level of effectiveness as a professional steam cleaning system. The professional, high-powered system used at Canada Clean Home ensures that your furniture is not left sopping wet or coated in unwanted residue."
      },
      {
        question: "Will steam cleaning remove stains from my furniture?",
        answer: "Yes. Steam cleaning is extremely effective at removing stains."
      },
      {
        question: "When will I be able to use my furniture again after professional cleaning?",
        answer: "We recommend allowing your furniture at least 4 hours of drying time before resuming regular use."
      },
      {
        question: "Do I need to do anything to prepare for upholstered furniture cleaning?",
        answer: "Furniture should be vacuumed and free from any surface debris."
      },
      {
        question: "Can steam cleaning remove bed bugs?",
        answer: "No. While steam cleaning can effectively remove dust mites, fleas and flea eggs, bed bugs require the employment of an exterminator."
      },
      {
        question: "How much does furniture cleaning cost?",
        answer: "The pricing for furniture starts at $49 and varies depending on the size of each piece and how many pieces of furniture are requiring maintenance."
      }
    ]
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
        </header>

        {/* Category Tabs */}
        <nav className="flex justify-center mb-12" aria-label="FAQ Categories">
          <div className="flex bg-white rounded-lg p-1 shadow-sm">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-md font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === category.id
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </nav>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs[activeCategory as keyof typeof faqs].map((faq, index) => (
            <article key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <h4>
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors cursor-pointer"
                  aria-expanded={openFAQ === index}
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <i className={`ri-${openFAQ === index ? 'subtract' : 'add'}-line text-xl text-gray-500`}></i>
                  </div>
                </button>
              </h4>
              
              {openFAQ === index && (
                <div className="px-6 pb-4">
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
